import React, { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Layers, Play, CheckCircle2, XCircle, Clock, Loader2, Plus } from 'lucide-react';

export default function BatchPage() {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [form, setForm] = useState({ gen_type: 'image', template_id: 'product-hero', prompt: '', variants: 6, platforms: ['meta'], duration: 5 });
  const [creating, setCreating] = useState(false);

  const load = async () => {
    try { setBatches(await api.listBatches(20)); } catch {} finally { setLoading(false); }
  };
  useEffect(() => { load(); const i = setInterval(load, 5000); return () => clearInterval(i); }, []);

  const handleCreate = async () => {
    if (!form.template_id) { toast.error('Select a template'); return; }
    setCreating(true);
    try {
      await api.createBatch(form);
      toast.success('Batch started!');
      setShowCreate(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setCreating(false); }
  };

  const loadBatchDetail = async (batchId) => {
    try { setSelectedBatch(await api.getBatch(batchId)); } catch {}
  };

  const TEMPLATES = ['product-hero', 'social-post', 'banner-set', 'email-hero', 'product-reveal', 'lifestyle', 'hook-pattern'];
  const PLATFORMS = ['meta', 'tiktok', 'youtube', 'google', 'linkedin'];

  const togglePlatform = (p) => {
    setForm(f => ({
      ...f,
      platforms: f.platforms.includes(p) ? f.platforms.filter(x => x !== p) : [...f.platforms, p]
    }));
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-[#CDFE65] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="animate-fade-in-up opacity-0" data-testid="batch-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-black tracking-tight" style={{ fontFamily: 'Chivo' }}>Batch <span className="text-[#06B6D4]">Processing</span></h1>
          <p className="text-white/40 mt-1">Generate for all platforms at once</p>
        </div>
        <button onClick={() => setShowCreate(true)} className="btn-primary text-sm flex items-center gap-2" data-testid="create-batch-btn">
          <Plus className="w-4 h-4" /> New Batch
        </button>
      </div>

      {/* Batch List */}
      {batches.length === 0 ? (
        <div className="bg-[#0F0F11] border border-white/5 rounded-xl p-16 text-center">
          <Layers className="w-12 h-12 text-white/10 mx-auto mb-4" />
          <p className="text-white/40 mb-4">No batch jobs yet. Create one to generate at scale.</p>
          <button onClick={() => setShowCreate(true)} className="btn-primary text-sm">Create Batch</button>
        </div>
      ) : (
        <div className="space-y-3">
          {batches.map(batch => {
            const progress = batch.total_generations > 0 ? Math.round(((batch.completed_generations + batch.failed_generations) / batch.total_generations) * 100) : 0;
            return (
              <div key={batch.id} className="bg-[#0F0F11] border border-white/5 rounded-xl p-5 card-hover cursor-pointer" onClick={() => loadBatchDetail(batch.id)} data-testid={`batch-${batch.id}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <span className={`text-xs px-2 py-0.5 rounded font-bold uppercase ${batch.status === 'completed' ? 'bg-green-500/10 text-green-400' : batch.status === 'running' ? 'bg-yellow-500/10 text-yellow-400' : batch.status === 'failed' ? 'bg-red-500/10 text-red-400' : 'bg-white/5 text-white/40'}`}>{batch.status}</span>
                    <span className="text-sm font-medium">{batch.template_id}</span>
                    <span className="text-xs text-white/30 font-mono">{batch.gen_type}</span>
                  </div>
                  <span className="text-xs text-white/30 font-mono">{batch.created_at?.slice(0, 16)}</span>
                </div>
                <div className="flex items-center gap-4 text-xs text-white/40 font-mono mb-3">
                  <span>{batch.variants} variants</span>
                  <span>{(batch.platforms || []).join(', ')}</span>
                  <span>{batch.total_generations} total</span>
                  <span className="text-green-400">{batch.completed_generations} done</span>
                  {batch.failed_generations > 0 && <span className="text-red-400">{batch.failed_generations} failed</span>}
                </div>
                <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                  <div className="h-full bg-[#CDFE65] rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Batch Detail Modal */}
      {selectedBatch && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setSelectedBatch(null)} data-testid="batch-detail-modal">
          <div className="bg-[#0F0F11] border border-white/10 rounded-2xl p-6 w-full max-w-3xl mx-4 max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <h2 className="text-xl font-bold mb-4">Batch: {selectedBatch.template_id} ({selectedBatch.status})</h2>
            <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
              {(selectedBatch.generations || []).map(gen => (
                <div key={gen.id} className="relative group overflow-hidden rounded-lg border border-white/5">
                  {gen.output_url ? (
                    <img src={gen.output_url} alt="" className="w-full aspect-square object-cover" loading="lazy" />
                  ) : (
                    <div className="w-full aspect-square bg-[#18181B] flex items-center justify-center">
                      {gen.status === 'processing' ? <Loader2 className="w-4 h-4 animate-spin text-[#CDFE65]" /> : <XCircle className="w-4 h-4 text-red-400" />}
                    </div>
                  )}
                  <div className="absolute bottom-0 inset-x-0 p-1 bg-black/60 text-[8px] font-mono text-white/60">
                    {gen.platform} · {gen.size}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Create Batch Modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="create-batch-modal">
          <div className="bg-[#0F0F11] border border-white/10 rounded-2xl p-6 w-full max-w-md mx-4 animate-fade-in-up opacity-0">
            <h2 className="text-xl font-bold mb-6">Create Batch Job</h2>
            <div className="space-y-4">
              <div>
                <label className="text-xs uppercase tracking-wider text-white/40 font-mono mb-1.5 block">Type</label>
                <div className="flex gap-2">
                  {['image', 'video'].map(t => (
                    <button key={t} onClick={() => setForm(f => ({ ...f, gen_type: t }))} className={`flex-1 py-2 rounded-lg text-sm font-bold ${form.gen_type === t ? 'bg-[#CDFE65] text-black' : 'bg-white/5 text-white/40'}`}>{t}</button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-white/40 font-mono mb-1.5 block">Template</label>
                <select value={form.template_id} onChange={e => setForm(f => ({ ...f, template_id: e.target.value }))} className="forge-input w-full" data-testid="batch-template-select">
                  {TEMPLATES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-white/40 font-mono mb-1.5 block">Prompt</label>
                <textarea value={form.prompt} onChange={e => setForm(f => ({ ...f, prompt: e.target.value }))} rows={3} className="forge-input w-full h-auto py-3 resize-none" placeholder="Describe your creative..." data-testid="batch-prompt-input" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-white/40 font-mono mb-1.5 block">Variants: {form.variants}</label>
                <input type="range" min="1" max="20" value={form.variants} onChange={e => setForm(f => ({ ...f, variants: parseInt(e.target.value) }))} className="w-full" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-white/40 font-mono mb-1.5 block">Platforms</label>
                <div className="flex flex-wrap gap-2">
                  {PLATFORMS.map(p => (
                    <button key={p} onClick={() => togglePlatform(p)} className={`text-xs px-3 py-1.5 rounded-full font-bold transition-all ${form.platforms.includes(p) ? 'bg-[#CDFE65] text-black' : 'bg-white/5 text-white/40 hover:text-white'}`} data-testid={`platform-toggle-${p}`}>
                      {p}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex gap-3">
                <button onClick={() => setShowCreate(false)} className="btn-secondary flex-1 text-sm">Cancel</button>
                <button onClick={handleCreate} disabled={creating} className="btn-primary flex-1 text-sm" data-testid="batch-submit-btn">
                  {creating ? 'Starting...' : 'Start Batch'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
